import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  HelpCircle, ChevronRight, ChevronDown, ChevronUp, Download,
  Lock, BadgeCheck, Building2, ShieldCheck, CheckCircle2, Folder, RefreshCw,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import { AppButton, EmptyState, PageHeader } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import useRazorpay, { PLATFORM_MERCHANT } from "../../hooks/useRazorpay";
import useHostedCheckout from "../../hooks/useHostedCheckout";
import HostedCheckoutModal from "../Billing/HostedCheckoutModal";
import { resolveBrandName } from "../../constants/brand";
import { priceLabel, isPaid, noCopyProps } from "./marketplaceShared";
import "./Marketplace.scss";

const TYPE_LABELS = {
  single_choice: "Single Choice",
  multi_select: "Multi Select",
  true_false: "True / False",
  text_answer: "Text Answer",
  fill_blank: "Fill in Blank",
  numeric_answer: "Numeric",
  image_mcq: "Image MCQ",
  code_conceptual: "Code Conceptual",
};

const BRAND = resolveBrandName("LMS Campus");
const TABS = ["Preview Questions", "Topics", "Details", "Reviews"];

/**
 * MarketplaceQuizPackPage — one published quiz pack. Left: pack info card
 * (stats, publisher, price/license/updated, Install / Buy / Update CTA).
 * Right: tabs — Preview Questions (5, expandable, correct answer shown,
 * copy blocked), Topics (subcategories + counts), Details, Reviews (soon).
 */
const MarketplaceQuizPackPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { openCheckout } = useRazorpay();
  const canBuy = can("marketplace.purchase");

  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState(TABS[0]);
  const [openQ, setOpenQ] = useState(0);
  const [busy, setBusy] = useState(""); // install | buy | update
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchPack = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/quiz-categories/marketplace-pack/${id}`);
      setPack(data.pack);
    } catch {
      setNotFound(true);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchPack(); }, [fetchPack]);

  // On a custom domain the platform Razorpay account refuses the in-page
  // checkout, so the pack is paid for in a new tab on the platform's domain.
  const hostedCheckout = useHostedCheckout({ onPaid: () => fetchPack() });

  const installPack = async () => {
    setBusy("install");
    try {
      const { data } = await apiClient.post("/quiz-questions/import-marketplace", { categoryId: id });
      setToast({ show: true, message: data.message || "Pack installed", type: "success" });
      await fetchPack();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Install failed", type: "error" });
    }
    setBusy("");
  };

  const updatePack = async () => {
    setBusy("update");
    try {
      const { data } = await apiClient.post("/quiz-questions/update-from-marketplace", { categoryId: id });
      setToast({ show: true, message: data.message || "Pack updated", type: "success" });
      await fetchPack();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Update failed", type: "error" });
    }
    setBusy("");
  };

  const buyPack = async () => {
    if (hostedCheckout.required) {
      hostedCheckout.start(`/quiz-categories/${id}/purchase-hosted-checkout`);
      return;
    }
    setBusy("buy");
    try {
      const { data } = await apiClient.post(`/quiz-categories/${id}/purchase-order`);
      await openCheckout(
        {
          razorpayKeyId: data.keyId,
          order: { id: data.orderId, amount: data.amount, currency: data.currency },
          merchantName: PLATFORM_MERCHANT,
          description: `Quiz pack: ${pack?.name || ""}`,
        },
        async (resp) => {
          try {
            await apiClient.post("/quiz-categories/purchase-verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setToast({ show: true, message: "Payment successful — pack unlocked!", type: "success" });
            await fetchPack();
          } catch (e) {
            setToast({ show: true, message: e.response?.data?.message || "Verification failed", type: "error" });
          }
          setBusy("");
        },
        (err) => {
          setToast({ show: true, message: err.reason || "Payment cancelled", type: "error" });
          setBusy("");
        },
      );
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Could not start the purchase", type: "error" });
      setBusy("");
    }
  };

  if (loading) return <div className="mkp-root"><div className="mkp-loading"><Spinner animation="border" size="sm" /></div></div>;
  if (notFound || !pack) {
    return (
      <div className="mkp-root">
        <EmptyState
          icon={<HelpCircle size={40} />}
          title="Pack not found"
          description="This pack may have been unpublished."
          action={<AppButton variant="outline" onClick={() => navigate("/marketplace/quiz-banks")}>Back to Quiz Banks</AppButton>}
        />
      </div>
    );
  }

  const paid = isPaid(pack.pricing);
  const locked = paid && !pack.entitled;
  const dateLabel = pack.lastUpdated
    ? new Date(pack.lastUpdated).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  const cta = locked ? canBuy && (
    <AppButton variant="default" disabled={busy === "buy"} onClick={buyPack}>
      <Lock size={15} /> {busy === "buy" ? "Opening checkout…" : `Buy ${priceLabel(pack.pricing)}`}
    </AppButton>
  ) : pack.installed.updates > 0 ? (
    <>
      <AppButton variant="default" disabled={busy === "update"} onClick={updatePack}>
        <RefreshCw size={15} /> {busy === "update" ? "Updating…" : `Update ${pack.installed.updates} question${pack.installed.updates === 1 ? "" : "s"}`}
      </AppButton>
      <AppButton variant="outline" disabled={busy === "install"} onClick={installPack}>
        <Download size={15} /> {busy === "install" ? "Installing…" : "Re-install pack"}
      </AppButton>
    </>
  ) : pack.installed.imported > 0 ? (
    <>
      <AppButton variant="outline" disabled>
        <CheckCircle2 size={15} /> Installed — up to date
      </AppButton>
      <AppButton variant="outline" disabled={busy === "install"} onClick={installPack}>
        <Download size={15} /> {busy === "install" ? "Installing…" : "Install new questions"}
      </AppButton>
    </>
  ) : (
    <AppButton variant="default" disabled={busy === "install" || pack.totalCount === 0} onClick={installPack}>
      <Download size={15} /> {busy === "install" ? "Installing…" : "Install Pack"}
    </AppButton>
  );

  return (
    <div className="mkp-root">
      {/* Title renders in the TopNav via the PageHeader portal. */}
      <PageHeader
        icon={HelpCircle}
        backButton
        onBack={() => navigate("/marketplace/quiz-banks")}
        title={pack.name}
        description="Quiz pack — preview, topics and installation"
      />

      <nav className="mkp-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate("/marketplace")}>Marketplace</button>
        <ChevronRight size={13} />
        <button type="button" onClick={() => navigate("/marketplace/quiz-banks")}>Quiz Banks</button>
        <ChevronRight size={13} />
        <span className="mkp-crumb-current">{pack.name}</span>
      </nav>

      <div className="mkp-pack">
        {/* ── Left: pack info ── */}
        <div>
          <div className="mkp-pack-card">
            <div className="mkp-pack-iconrow">
              <span className="mkp-pack-icon">{pack.icon || <HelpCircle size={26} />}</span>
              <div className="mkp-pack-titlebox">
                <h2 className="mkp-pack-title">
                  {pack.name}
                  <span className="mkp-pack-version">v{pack.version}</span>
                </h2>
                <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                  <span className="mkp-chip mkp-chip--type">Quiz</span>
                  {pack.installed.imported > 0 && (
                    <span className={`mkp-chip ${pack.installed.updates > 0 ? "mkp-chip--update" : "mkp-chip--installed"}`}>
                      {pack.installed.updates > 0 ? `${pack.installed.updates} update${pack.installed.updates === 1 ? "" : "s"} available` : "Installed"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {pack.description && <p className="mkp-pack-desc">{pack.description}</p>}

            <div className="mkp-pack-stats">
              <div className="mkp-pack-stat">
                <div className="mkp-stat-value">{pack.totalCount}</div>
                <div className="mkp-stat-label">Questions</div>
              </div>
              <div className="mkp-pack-stat">
                <div className="mkp-stat-value">{pack.topics.length}</div>
                <div className="mkp-stat-label">Topics</div>
              </div>
              <div className="mkp-pack-stat">
                <div className="mkp-stat-value">{pack.installs >= 1000 ? `${(pack.installs / 1000).toFixed(1)}K+` : pack.installs}</div>
                <div className="mkp-stat-label">Installs</div>
              </div>
            </div>

            <div>
              <div className="mkp-filter-label" style={{ marginBottom: "0.4rem" }}>Publisher</div>
              <div className="mkp-pack-publisher">
                <span className="mkp-pub-icon"><Building2 size={20} /></span>
                <div>
                  <div className="mkp-pub-name">
                    {BRAND} <BadgeCheck size={14} className="mkp-pub-check" />
                  </div>
                  <div className="mkp-pub-sub">Official Publisher</div>
                </div>
              </div>
            </div>

            <div className="mkp-pack-meta">
              <div className="mkp-meta-row">
                <span className="mkp-meta-key">Price</span>
                <span className={`mkp-price ${paid ? "mkp-price--paid" : "mkp-price--free"}`}>{priceLabel(pack.pricing)}</span>
              </div>
              <div className="mkp-meta-row">
                <span className="mkp-meta-key">License</span>
                <span className="mkp-meta-val">Single Institute</span>
              </div>
              <div className="mkp-meta-row">
                <span className="mkp-meta-key">Last Updated</span>
                <span className="mkp-meta-val">{dateLabel}</span>
              </div>
              <div className="mkp-meta-row">
                <span className="mkp-meta-key">Language</span>
                <span className="mkp-meta-val">English</span>
              </div>
              <div className="mkp-meta-row">
                <span className="mkp-meta-key">Compatible With</span>
                <span className="mkp-meta-val">{BRAND} v1.0+</span>
              </div>
            </div>

            <div className="mkp-pack-cta">{cta}</div>
          </div>

          <div className="mkp-trust">
            <ShieldCheck size={18} className="mkp-trust-icon" />
            <div>
              <div className="mkp-trust-title">Trusted content</div>
              <div className="mkp-trust-sub">All content is reviewed and verified by {BRAND}.</div>
            </div>
          </div>
        </div>

        {/* ── Right: tabs ── */}
        <div className="mkp-tabs-card">
          <div className="mkp-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={`mkp-tab ${tab === t ? "mkp-tab--active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mkp-tab-body">
            {tab === "Preview Questions" && (
              <>
                <h3 className="mkp-tab-title">
                  Preview Questions <span>({pack.previewQuestions.length} of {pack.totalCount})</span>
                </h3>
                {pack.previewQuestions.length === 0 ? (
                  <div className="mkp-empty">No public questions in this pack yet.</div>
                ) : (
                  <div className="mkp-preview" {...noCopyProps}>
                    {pack.previewQuestions.map((q, i) => {
                      const open = openQ === i;
                      const isChoice = (q.options || []).length > 0;
                      const correct = (q.options || []).filter((o) => o.isCorrect);
                      return (
                        <div className="mkp-qitem" key={q._id}>
                          <button type="button" className="mkp-qhead" onClick={() => setOpenQ(open ? -1 : i)}>
                            <span className="mkp-qnum">{i + 1}</span>
                            <span className="mkp-qtext">
                              {q.questionText}
                              {q.topicName && <span className="mkp-qtopic"><Folder size={10} /> {q.topicName}</span>}
                            </span>
                            <span className={`mkp-chip mkp-chip--${q.difficulty}`}>
                              {q.difficulty?.[0]?.toUpperCase()}{q.difficulty?.slice(1)}
                            </span>
                            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                          {open && (
                            <div className="mkp-qbody">
                              {isChoice ? (
                                <>
                                  {q.options.map((o, oi) => (
                                    <div key={oi} className={`mkp-qopt ${o.isCorrect ? "mkp-qopt--correct" : ""}`}>
                                      <span className="mkp-qradio" />
                                      <span>{String.fromCharCode(65 + oi)}. {o.text}</span>
                                    </div>
                                  ))}
                                  <div className="mkp-qanswer">
                                    <div className="mkp-qanswer-head">
                                      <CheckCircle2 size={14} />
                                      Correct Answer: {correct.map((o) => `${String.fromCharCode(65 + q.options.indexOf(o))}. ${o.text}`).join(", ")}
                                    </div>
                                    {q.explanation && <div className="mkp-qanswer-expl">{q.explanation}</div>}
                                  </div>
                                </>
                              ) : (
                                <div className="mkp-qmeta-answer">
                                  <em>{TYPE_LABELS[q.questionType] || q.questionType}</em> question
                                  {q.explanation ? ` — ${q.explanation}` : " — install the pack to see the full answer key."}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {tab === "Topics" && (
              <>
                <h3 className="mkp-tab-title">Topics <span>({pack.topics.length})</span></h3>
                {pack.topics.length === 0 && pack.directCount === 0 ? (
                  <div className="mkp-empty">No topics in this pack.</div>
                ) : (
                  <div className="mkp-topics">
                    {pack.topics.map((t) => (
                      <div className="mkp-topic-row" key={t._id}>
                        <Folder size={16} className="mkp-topic-icon" />
                        <span className="mkp-topic-name">{t.icon ? `${t.icon} ` : ""}{t.name}</span>
                        <span className="mkp-topic-count">{t.count} question{t.count === 1 ? "" : "s"}</span>
                      </div>
                    ))}
                    {pack.directCount > 0 && (
                      <div className="mkp-topic-row">
                        <Folder size={16} className="mkp-topic-icon" />
                        <span className="mkp-topic-name">General (directly in {pack.name})</span>
                        <span className="mkp-topic-count">{pack.directCount} question{pack.directCount === 1 ? "" : "s"}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {tab === "Details" && (
              <>
                <h3 className="mkp-tab-title">Details</h3>
                <div className="mkp-details">
                  <p>{pack.description || "No description provided."}</p>
                  <p>
                    This pack contains <strong>{pack.totalCount} questions</strong> across{" "}
                    <strong>{pack.topics.length} topics</strong>. Installing clones the questions and folder
                    structure into your own Question Bank — you can edit, reorganize, or delete them freely.
                    When the publisher updates the pack, an <strong>Update</strong> button appears here to pull
                    the new versions into your copies without losing your stats.
                  </p>
                  <p>
                    Version v{pack.version} · Last updated {dateLabel}
                    {pack.publishedAt ? ` · Published ${new Date(pack.publishedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                  </p>
                </div>
              </>
            )}

            {tab === "Reviews" && (
              <div className="mkp-reviews-soon">Reviews are coming soon.</div>
            )}
          </div>
        </div>
      </div>

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      <HostedCheckoutModal checkout={hostedCheckout} />
    </div>
  );
};

export default MarketplaceQuizPackPage;
